import { SocialMessageType } from 'hooks/useWebsocketTypes';
import { updateFeedNews } from 'redux/utils/feedUtils';
import { FeedItemType } from 'types/feedTypes';
import { ActionType, PayloadErrorType } from 'types/reduxTypes';
import { FeedStateType } from 'types/stateTypes';
import { idOnly } from 'utils/cls.utils';
import { AnswerNewsPayloadType, UpdatePostWsSuccessType } from '../actions/feedActions';
import {
  CHANGE_SUBSCRIPTION,
  CHANGE_SUBSCRIPTION_FAILED,
  CHANGE_SUBSCRIPTION_SUCCESS,
  REQUEST_OWN_FEEDS_SUCCESS,
  REQUEST_SUBSCRIPTION,
  REQUEST_SUBSCRIPTION_FAILED,
  REQUEST_SUBSCRIPTION_SUCCESS,
  REQUEST_USER_NEWS,
  REQUEST_USER_NEWS_FAILED,
  REQUEST_USER_NEWS_SUCCESS,
  UPDATE_FEED_POST_REACTIONS,
  UPDATE_POST_STATS_WS,
  UPDATE_POST_WS_SUCCESS,
} from '../constants';

const initialState: FeedStateType = {
  error: false,
  errorText: '',
  hasMore: true,
  isUpdateSubscriptionFirst: false,
  loading: true,
  news: [],
  offlineNews: [],
  ownFeeds: [],
  subscription: [],
};

export type FeedPayloadType = FeedStateType | AnswerNewsPayloadType | SocialMessageType | PayloadErrorType | string;

export default (state = initialState, action: ActionType<FeedPayloadType>): FeedStateType => {
  switch (action.type) {
    case REQUEST_SUBSCRIPTION: {
      return { ...state, loading: true };
    }
    case REQUEST_SUBSCRIPTION_SUCCESS: {
      const { isUpdateSubscriptionFirst: forOffline, subscription } = action.payload as FeedStateType;
      return {
        ...state,
        isUpdateSubscriptionFirst: forOffline,
        loading: false,
        subscription: subscription,
      };
    }
    case REQUEST_SUBSCRIPTION_FAILED: {
      const { message } = action.payload as PayloadErrorType;
      return { ...state, error: true, errorText: message, loading: false };
    }

    case CHANGE_SUBSCRIPTION: {
      return { ...state, loading: true };
    }
    case CHANGE_SUBSCRIPTION_SUCCESS: {
      const { subscription } = action.payload as FeedStateType;
      return { ...state, loading: false, subscription };
    }
    case CHANGE_SUBSCRIPTION_FAILED: {
      const { message } = action.payload as PayloadErrorType;
      return { ...state, error: true, errorText: message, loading: false };
    }
    case REQUEST_USER_NEWS: {
      return { ...state, loading: true };
    }

    case REQUEST_USER_NEWS_FAILED: {
      const { message } = action.payload as PayloadErrorType;
      return { ...state, error: true, errorText: message, loading: false };
    }

    case REQUEST_USER_NEWS_SUCCESS: {
      const { data, mode, isBrowserOnline, pageSize } = action.payload as AnswerNewsPayloadType;
      const currentNews = state.news;
      const hasMore = data.length === pageSize;
      if (mode === 'append') {
        return { ...state, hasMore, loading: false, news: currentNews.concat(data as FeedItemType[]) };
      }
      if (mode === 'replace') {
        if (isBrowserOnline === undefined) {
          return { ...state, hasMore, loading: false, news: data };
        }
        return {
          ...state,
          loading: false,
          offlineNews: data,
        };
      }
      return state;
    }

    case REQUEST_OWN_FEEDS_SUCCESS: {
      return { ...state, ownFeeds: action.payload as unknown as FeedStateType['ownFeeds'] };
    }

    case UPDATE_POST_STATS_WS: {
      const payload = action.payload as SocialMessageType;
      const newFeeds = state.news.map((feed) =>
        feed.body.id === idOnly(payload.rObject)
          ? { ...feed, body: { ...feed.body, meta: { ...feed.body.meta, ...payload.payload } } }
          : feed
      );
      return { ...state, news: newFeeds as FeedItemType[] };
    }

    case UPDATE_POST_WS_SUCCESS: {
      const { postId, post, actionType } = action.payload as unknown as UpdatePostWsSuccessType;
      const newFeeds =
        actionType === 'update' && post
          ? state.news.map((feed) => (feed.body.id === postId ? { ...feed, body: { ...feed.body, ...post } } : feed))
          : state.news.filter((feed) => feed.body.id !== postId);
      return { ...state, news: newFeeds };
    }

    case UPDATE_FEED_POST_REACTIONS: {
      const postId = action.payload as string;
      return {
        ...state,
        news: updateFeedNews(state.news, postId),
      };
    }

    default: {
      return state;
    }
  }
};
